import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppConfig } from 'src/app/configs/app.config';
import { EventType } from 'src/app/constants/events-type';
import { EventManagerService } from 'src/app/core/services/event-manager/event-manager.service';
import { EventContent } from 'src/app/core/services/event-manager/models/event-content';

/** Delay so NVDA finishes the control that triggered preloader (WCAG 4.1.3). */
const PRELOADER_A11Y_ANNOUNCE_DELAY_MS = 120;

@Component({
  selector: 'app-preloader',
  templateUrl: './preloader.component.html',
  styleUrls: ['./preloader.component.scss']
})
export class PreloaderComponent implements OnInit, OnDestroy {
  private subscription: Subscription | undefined;
  shouldShowPreloader = false;
  debounce: ReturnType<typeof setTimeout> | undefined;
  private preloaderAnnounceTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private eventManager: EventManagerService,
    private liveAnnouncer: LiveAnnouncer,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscription = this.eventManager.subscribe(EventType.PreloaderShow, (eventContent: EventContent<boolean>) => {
      this.onPreloaderVisibility(eventContent);
    });
  }

  ngOnDestroy(): void {
    this.clearPreloaderAnnounceTimeout();
    if (this.subscription) {
      this.eventManager.destroy(this.subscription);
    }
  }

  private clearPreloaderAnnounceTimeout(): void {
    if (this.preloaderAnnounceTimeoutId != null) {
      clearTimeout(this.preloaderAnnounceTimeoutId);
      this.preloaderAnnounceTimeoutId = null;
    }
  }

  private onPreloaderVisibility(eventContent: EventContent<boolean>): void {
    // Prevents further executution if there is no actual change
    if (this.shouldShowPreloader === eventContent.content) {
      return;
    }

    if (eventContent.content === false) {
      this.clearPreloaderAnnounceTimeout();
      clearTimeout(this.debounce);

      this.debounce = setTimeout(() => {
        this.shouldShowPreloader = eventContent.content;
      }, AppConfig.Preloader.debounceTimeMs);
    } else {
      this.shouldShowPreloader = eventContent.content;
      this.clearPreloaderAnnounceTimeout();
      this.preloaderAnnounceTimeoutId = setTimeout(() => {
        this.preloaderAnnounceTimeoutId = null;
        this.liveAnnouncer.announce(
          this.translate.instant('A11Y_LOADING_PLEASE_WAIT'),
          'polite'
        );
      }, PRELOADER_A11Y_ANNOUNCE_DELAY_MS);
    }
  }
}
